/**
 * 86'd Products Manager - Schnelle Produktverfügbarkeit
 * Restaurant-Jargon "86'd" = ausverkauft/nicht verfügbar
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Ban, CheckCircle, Search, RefreshCw, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { quickDisableProduct } from '../utils/orderValidation';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function Products86Manager() {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredProducts(products);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredProducts(
        products.filter(p =>
          p.name.toLowerCase().includes(query) ||
          p.category?.toLowerCase().includes(query)
        )
      );
    }
  }, [searchQuery, products]);

  const loadProducts = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE}/api/cashx/products`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setProducts(data.products || []);
        setFilteredProducts(data.products || []);
      }
    } catch (error) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const toggle86 = async (productId, currentStatus, productName) => {
    const newDisabled = !currentStatus;
    const success = await quickDisableProduct(
      productId,
      newDisabled,
      newDisabled ? 'Heute ausverkauft' : ''
    );

    if (success) {
      toast.success(
        newDisabled 
          ? `${productName} als ausverkauft markiert` 
          : `${productName} wieder verfügbar`
      );
      loadProducts();
    } else {
      toast.error('Fehler');
    }
  };

  const unavailableCount = products.filter(p => !p.is_available).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B1220] to-[#0E1628] p-6">
      <div className="max-w-6xl mx-auto">
        
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Produktverfügbarkeit (86'd)
          </h1>
          <p className="text-slate-400">
            Produkte schnell als ausverkauft markieren
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="bg-[#111C32]/80 border-slate-700/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-8 h-8 text-emerald-400" />
                <div>
                  <div className="text-2xl font-bold text-white">
                    {products.length - unavailableCount}
                  </div>
                  <div className="text-xs text-slate-400">Verfügbar</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#111C32]/80 border-slate-700/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Ban className="w-8 h-8 text-red-400" />
                <div>
                  <div className="text-2xl font-bold text-white">
                    {unavailableCount}
                  </div>
                  <div className="text-xs text-slate-400">Ausverkauft</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#111C32]/80 border-slate-700/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <RefreshCw className="w-8 h-8 text-teal-400" />
                <div>
                  <Button
                    onClick={loadProducts}
                    disabled={loading}
                    className="bg-teal-500 hover:bg-teal-600 text-white h-10"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Aktualisieren
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <Card className="bg-[#111C32] border-slate-700/50 mb-6">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Produkt suchen..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#0E1628] border-slate-700 text-white h-12"
                data-testid="product-search"
              />
            </div>
          </CardContent>
        </Card>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProducts.map((product) => {
            const is86d = !product.is_available;

            return (
              <Card
                key={product.product_id}
                className={`
                  border-2 transition-all
                  ${is86d
                    ? 'bg-red-500/10 border-red-500/40'
                    : 'bg-[#111C32] border-slate-700/50'
                  }
                `}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-white text-lg mb-1">
                        {product.name}
                      </CardTitle>
                      <CardDescription className="text-slate-400">
                        {product.category}
                      </CardDescription>
                      {product.unavailable_reason && (
                        <div className="mt-2 text-xs text-red-400 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {product.unavailable_reason}
                        </div>
                      )}
                    </div>
                    
                    {is86d && (
                      <Badge className="bg-red-500/30 text-red-300 border-red-500/50">
                        <Ban className="w-3 h-3 mr-1" />
                        86'd
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="text-2xl font-bold text-teal-400" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                      {product.price?.toFixed(2)}€
                    </div>
                    
                    <Button
                      onClick={() => toggle86(product.product_id, is86d, product.name)}
                      size="sm"
                      className={`
                        h-10 px-4 font-semibold transition-all
                        ${is86d
                          ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                          : 'bg-red-500 hover:bg-red-600 text-white'
                        }
                      `}
                      data-testid={`toggle-86-${product.product_id}`}
                    >
                      {is86d ? (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Wieder verfügbar
                        </>
                      ) : (
                        <>
                          <Ban className="w-4 h-4 mr-2" />
                          Als 86'd markieren
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {filteredProducts.length === 0 && !loading && (
          <Card className="bg-[#111C32]/50 border-slate-700/30">
            <CardContent className="p-12 text-center">
              <Search className="w-16 h-16 mx-auto mb-4 text-slate-600" />
              <p className="text-slate-400">
                {searchQuery ? 'Keine Produkte gefunden' : 'Keine Produkte vorhanden'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
